import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/apiClient';

interface ActiveUser {
  name: string;
  account_type: string;
  account_type_display: string;
  current_page: string;
  city: string;
  state: string;
  country: string;
  device: string;
  last_seen: string;
  last_seen_seconds: number;
}

interface ActiveUsersData {
  active_count: number;
  by_type: {
    staff: number;
    customer_portal: number;
    bridge_club: number;
    store_club: number;
  };
  users: ActiveUser[];
}

const ActiveUsersWidget: React.FC = () => {
  const [data, setData] = useState<ActiveUsersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasDataRef = useRef(false);

  const loadData = useCallback(async () => {
    if (!navigator.onLine || document.hidden) return;
    try {
      const response = await api.get<ActiveUsersData>('/api/admin/active-users/');
      setData(response);
      hasDataRef.current = true;
      setError(null);
    } catch (err) {
      // Silently fail — polling will retry
      if (!hasDataRef.current) setError('Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const getAccountTypeColor = (type: string): string => {
    switch (type) {
      case 'staff':
        return '#2563eb'; // Blue
      case 'customer_portal':
        return '#059669'; // Green
      case 'bridge_club':
        return '#d97706'; // Amber
      case 'store_club':
        return '#7c3aed'; // Purple
      default:
        return '#6b7280'; // Gray
    }
  };

  const getAccountTypeIcon = (type: string): string => {
    switch (type) {
      case 'staff':
        return '👔';
      case 'customer_portal':
        return '🏭';
      case 'bridge_club':
        return '🌉';
      case 'store_club':
        return '🏪';
      default:
        return '👤';
    }
  };

  const formatPageName = (page: string): string => {
    if (!page) return 'Unknown';
    // Extract meaningful part of URL
    const parts = page.split('/').filter(Boolean);
    if (parts.length === 0) return 'Home';
    // Capitalize and format
    const lastPart = parts[parts.length - 1];
    return lastPart
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading && !data) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <span style={styles.icon}>👥</span>
          <span style={styles.title}>Active Users</span>
        </div>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={styles.widget}>
        <div style={styles.header}>
          <span style={styles.icon}>👥</span>
          <span style={styles.title}>Active Users</span>
        </div>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={styles.widget}>
      <div
        style={styles.header}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
      >
        <div style={styles.headerLeft}>
          <span style={styles.icon}>👥</span>
          <span style={styles.title}>Active Users</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.count}>{data.active_count}</span>
          <span style={styles.countLabel}>online</span>
          <span style={styles.expandIcon}>{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Summary by type */}
      <div style={styles.typeSummary}>
        {data.by_type.staff > 0 && (
          <span style={{ ...styles.typeBadge, backgroundColor: '#dbeafe', color: '#1e40af' }}>
            👔 {data.by_type.staff} Staff
          </span>
        )}
        {data.by_type.customer_portal > 0 && (
          <span style={{ ...styles.typeBadge, backgroundColor: '#d1fae5', color: '#065f46' }}>
            🏭 {data.by_type.customer_portal} Portal
          </span>
        )}
        {data.by_type.bridge_club > 0 && (
          <span style={{ ...styles.typeBadge, backgroundColor: '#fef3c7', color: '#92400e' }}>
            🌉 {data.by_type.bridge_club} Bridge
          </span>
        )}
        {data.by_type.store_club > 0 && (
          <span style={{ ...styles.typeBadge, backgroundColor: '#ede9fe', color: '#5b21b6' }}>
            🏪 {data.by_type.store_club} Store
          </span>
        )}
      </div>

      {/* Expanded user list */}
      {expanded && (
        <div style={styles.userList}>
          {data.users.length === 0 ? (
            <div style={styles.emptyState}>No active users</div>
          ) : (
            data.users.map((user, idx) => (
              <div key={idx} style={styles.userRow}>
                <div style={styles.userIcon}>
                  {getAccountTypeIcon(user.account_type)}
                </div>
                <div style={styles.userInfo}>
                  <div style={styles.userName}>{user.name}</div>
                  <div style={styles.userMeta}>
                    <span
                      style={{
                        ...styles.userType,
                        color: getAccountTypeColor(user.account_type)
                      }}
                    >
                      {user.account_type_display}
                    </span>
                    {user.current_page && (
                      <>
                        <span style={styles.separator}>•</span>
                        <span style={styles.userPage}>{formatPageName(user.current_page)}</span>
                      </>
                    )}
                  </div>
                  <div style={styles.userLocation}>
                    {user.city && user.state ? (
                      <span>📍 {user.city}, {user.state}</span>
                    ) : user.device ? (
                      <span>💻 {user.device}</span>
                    ) : null}
                  </div>
                </div>
                <div style={styles.lastSeen}>
                  <span style={user.last_seen_seconds < 60 ? styles.recentDot : styles.normalDot}>●</span>
                  {user.last_seen}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  widget: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: '0.75rem',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  icon: {
    fontSize: '1.25rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1f2937',
  },
  count: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#059669',
  },
  countLabel: {
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  expandIcon: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginLeft: '0.25rem',
  },
  loading: {
    textAlign: 'center',
    color: '#9ca3af',
    padding: '1rem 0',
    fontSize: '0.9rem',
  },
  error: {
    textAlign: 'center',
    color: '#dc2626',
    padding: '1rem 0',
    fontSize: '0.9rem',
  },
  typeSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  typeBadge: {
    fontSize: '0.75rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '9999px',
    fontWeight: 500,
  },
  userList: {
    marginTop: '0.75rem',
    borderTop: '1px solid #e5e7eb',
    paddingTop: '0.75rem',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  userRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f3f4f6',
  },
  userIcon: {
    fontSize: '1.25rem',
    marginTop: '0.125rem',
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontWeight: 600,
    color: '#1f2937',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userMeta: {
    fontSize: '0.8rem',
    marginTop: '0.125rem',
  },
  userType: {
    fontWeight: 500,
  },
  separator: {
    margin: '0 0.25rem',
    color: '#d1d5db',
  },
  userPage: {
    color: '#6b7280',
  },
  userLocation: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    marginTop: '0.125rem',
  },
  lastSeen: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  recentDot: {
    color: '#22c55e',
    fontSize: '0.5rem',
  },
  normalDot: {
    color: '#d1d5db',
    fontSize: '0.5rem',
  },
  emptyState: {
    textAlign: 'center',
    color: '#9ca3af',
    padding: '1rem 0',
    fontSize: '0.85rem',
  },
};

export default ActiveUsersWidget;
